import React, { FC, useEffect, useState } from 'react'

import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { v4 as uuidv4 } from 'uuid'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core/dist/types'
import clsx from 'clsx'
import { isEmpty, sumBy } from 'lodash'

import MainModal from './common/MainModal'
import Input from '../common/inputs/Input'
import MessageHelperComp from '../../helpers/MessageHelper'
import { ScoreCardSection } from './components/Operations/ScoreCardSection/ScoreCardSection'
import { JumpOffToggle } from './components/Operations/JumpOffToggle/JumpOffToggle'
import { FenceHeight } from './components/Operations/FenceHeight/FenceHeight'

import FirestoreService from '../../services/firestoreService'
import { getConvertedData } from '../../models/interface.helper'
import { getUserFullName } from '../../helpers/helpers'
import useToasterHelper from '../../helpers/ToasterHelper'

import { useAppSelector } from '../../store/hooks'
import { selectProfileData } from '../../store/user/userSlice'

import { ScoreCardModel } from '../../models/score-cards/score-cards.model'
import { UserModel } from '../../models/users/user.model'

import {
  IScoreCardInterface,
  IScoreCardSection,
} from '../../models/score-cards/score-cards.interface'
import { IUserInterface } from '../../models/users/user.interface'

import { MODAL_CONSTS } from '../../const/modal-const'
import { CONST } from '../../const/const'
import { INITIAL_ROW, INITIAL_SECTION } from '../../const/score-card-const'
import { MESSAGES_CONST } from '../../const/messages-const'

const COLLECTIONS = CONST.DATA.FIRESTORE.LATEST.COLLECTIONS

interface AddScoreCardModalProps {
  handleModal: (showHide: boolean, typeOfModal: string, data?: any) => void
  dataToPassOn: {
    type: 'optimum' | 'fastest'
    scoreCardId?: string
    judges?: string[]
    editableScore: boolean
    isDuplicate?: boolean
  }
}

const scoreCardModelValues = new ScoreCardModel().toObject()
const scoreCardValidationSchema = ScoreCardModel.validationSchema()

export const AddScoreCardModal: FC<AddScoreCardModalProps> = ({ handleModal, dataToPassOn }) => {
  const { scoreCardId, judges, editableScore } = dataToPassOn
  const toastFunctions = useToasterHelper()

  const profileData = useAppSelector(selectProfileData)

  const [judge, setJudge] = useState<IUserInterface | null>(null)
  const [loading, setLoading] = React.useState(false)

  const {
    handleSubmit,
    register,
    watch,
    setValue,
    reset,
    trigger,
    formState: { errors },
  } = useForm<IScoreCardInterface>({
    defaultValues: scoreCardModelValues,
    resolver: yupResolver(scoreCardValidationSchema),
    mode: 'onChange',
  })

  const getScoreCardById = async (id: string) => {
    try {
      const scoreCardSnaps = await FirestoreService.getItem(COLLECTIONS.SCORE_CARDS.NAME, id)
      const scoreCard = getConvertedData(ScoreCardModel.fromFirestoreDoc(scoreCardSnaps).toObject())
      if (dataToPassOn.isDuplicate) {
        const { id, created, modified, ...rest } = scoreCard
        reset(rest)
      } else {
        reset(scoreCard)
      }
    } catch (e) {
      console.error(e)
    }
  }

  const getJudge = async (judgeId: string) => {
    try {
      const userSnaps = await FirestoreService.getItem(COLLECTIONS.USERS.NAME, judgeId)
      const user = getConvertedData(UserModel.fromFirestoreDoc(userSnaps).toObject())
      setJudge(user)
    } catch (e) {
      console.error(e)
    }
  }

  useEffect(() => {
    if (scoreCardId) getScoreCardById(scoreCardId).then()
  }, [scoreCardId])

  useEffect(() => {
    if (judges?.[0]) getJudge(judges[0]).then()
  }, [judges])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) return

    const sections = watch('sections')

    // Check if the dragged item is a row inside a section
    const sectionIndex = sections.findIndex((section) =>
      section.rows.some((row) => row.id === active.id)
    )

    if (sectionIndex !== -1) {
      const rows = sections[sectionIndex].rows

      const oldIndex = rows.findIndex((row) => row.id === active.id)
      const newIndex = rows.findIndex((row) => row.id === over.id)

      if (oldIndex !== -1 && newIndex !== -1) {
        const updatedRows = arrayMove(rows, oldIndex, newIndex)
        setValue(`sections.${sectionIndex}.rows`, updatedRows)
      }
    } else {
      // Handle drag-and-drop for sections
      const oldIndex = sections.findIndex((section) => section.id === active.id)
      const newIndex = sections.findIndex((section) => section.id === over.id)

      if (oldIndex !== -1 && newIndex !== -1) {
        const updatedSections = arrayMove(sections, oldIndex, newIndex)
        setValue('sections', updatedSections)
      }
    }
  }

  const onSubmit = async () => {
    // handleModal?.(true, MODAL_CONSTS.MAKE_SCORE_CARD_PUBLIC, { scoreCard: watch() })
    setLoading(true)
    try {
      if (watch()?.id) {
        await FirestoreService.updateItem(
          COLLECTIONS.SCORE_CARDS.NAME,
          watch().id,
          new ScoreCardModel({
            ...watch(),
            isPublic: false,
            modified: new Date().toString(),
          }).toFirestore()
        )
      } else {
        await FirestoreService.createItem(
          COLLECTIONS.SCORE_CARDS.NAME,
          new ScoreCardModel({
            ...watch(),
            isPublic: false,
            owner: profileData.id,
            scoreCardType: dataToPassOn.type,
            type: 'hunter-jumper',
          }).toFirestore()
        )
      }
      toastFunctions.success({ message: MESSAGES_CONST.SCORE_CARD_CREATED })
      handleModal?.(false, MODAL_CONSTS.ADD_SCORE_CARD)
    } catch (e) {
      console.error(e)
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
    } finally {
      setLoading(false)
    }
  }

  const onAddSection = () => {
    const sections = watch('sections').filter((section) => section.type !== 'jump off')
    const jumpOffSection = watch('sections').filter((section) => section.type === 'jump off')

    const newSections: IScoreCardSection[] = [
      ...sections,
      {
        id: uuidv4(),
        name: '',
        type: 'round',
        ...INITIAL_SECTION,
        rows: [{ id: uuidv4(), ...INITIAL_ROW }],
      },
      ...jumpOffSection,
    ]

    setValue('sections', newSections)
  }

  const onRemoveAllSections = () => {
    handleModal?.(true, MODAL_CONSTS.REMOVE_CONFIRMATION, {
      ...dataToPassOn,
      removeItem: () => setValue('sections', []),
    })
  }

  const onPreview = () => {
    if (isEmpty(errors) && watch('name')) {
      handleModal?.(true, MODAL_CONSTS.CUSTOM_SCORING, {
        ...dataToPassOn,
        rider: {
          score: {
            sheet: { name: watch('name') },
            judges: [
              {
                id: judge?.id ?? '',
                name: judge ? getUserFullName(judge) : '',
                picture: judge?.userProfilePicture ?? '',
                sections: [],
                customSections: watch('sections'),
                furtherRemarks: watch('furtherRemarks'),
                showJumpOff: watch('showJumpOff'),
                fenceHeight: watch('fenceHeight'),
                scoreCardType: dataToPassOn.type,
                signatureDate: null,
                signatureUrl: null,
              },
            ],
          },
        },
        editable: false,
      })
    }
  }

  return (
    <MainModal
      customTitle={
        <div className="text-SeabiscuitDark200ThemeColor flex flex-col md:flex-row items-center justify-between gap-2">
          <div>
            <h2 className="text-lg md:text-[25px] font-bold">
              {editableScore ? 'Add new course' : 'View course'}
            </h2>
            <p className="tex-sm md:text-[20px] text-SeabiscuitDark200ThemeColor/50">
              {editableScore
                ? 'Add sections and movements to build custom jumper course'
                : 'View sections and movements to build custom jumper course'}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <div className="text-right">
              <p className="text-sm text-SeabiscuitDark200ThemeColor/50">Preview</p>
              <p className="text-sm underline">
                {watch('sections').filter((section) => section.type !== 'jump off').length} rounds •{' '}
                {sumBy(
                  watch('sections').filter((section) => section.type !== 'jump off'),
                  (section) => section.rows.length
                )}{' '}
                jumps{' '}
                {watch('sections').filter((section) => section.type === 'jump off').length > 0 &&
                  watch('showJumpOff') &&
                  ` • ${watch('sections').filter((section) => section.type === 'jump off').length} Jump Off`}
              </p>
            </div>
            <button
              className={clsx(
                'w-11 p-2.5 bg-SeabiscuitGrayThemeColor rounded-lg transition-all hover:opacity-70',
                isEmpty(errors) && watch('name')
                  ? 'bg-SeabiscuitMainThemeColor/5 text-SeabiscuitMainThemeColor'
                  : 'bg-SeabiscuitGrayThemeColor'
              )}
              onClick={onPreview}
            >
              {isEmpty(errors) && watch('name') ? (
                <img className="w-full" src="/assets/og_icons/Full%20Screen-2.svg" alt="" />
              ) : (
                <img className="w-full" src="/assets/og_icons/FullScreen-1.svg" alt="" />
              )}
            </button>
          </div>
        </div>
      }
      show={true}
      type="ADD_SCORE_CARD"
      size="4xl"
      modalClassName={'!z-[30]'}
      overlayClassName={'!z-[20]'}
      buttons={[
        {
          hidden: !editableScore,
          label: 'SAVE SCORE CARD',
          fullWidth: true,
          loading,
          onClick: (e) => handleSubmit(onSubmit)(e),
        },
        {
          label: 'CANCEL',
          fullWidth: true,
          bgClass: 'bg-SeabiscuitLightThemeColor',
          className: 'outline-none',
          loading,
          borderClass: 'border border-transparent',
          textClass: 'text-SeabiscuitLightTextColor',
          onClick: () => handleModal?.(false, MODAL_CONSTS.ADD_SCORE_CARD),
        },
      ]}
    >
      <div className="text-SeabiscuitDark200ThemeColor pt-2">
        <div className="relative w-full flex items-center justify-between gap-2">
          <Input
            disabled={!editableScore}
            name="name"
            className="border-SeabiscuitGray500ThemeColor pr-36 py-3.5"
            placeholder="Course name"
            register={register}
            value={watch('name')}
            valid={!!watch('name')}
            validValueClassName={clsx(
              '!border-transparent font-bold',
              !!watch('name')
                ? 'bg-SeabiscuitMainThemeColor/5 text-SeabiscuitMainThemeColor'
                : 'bg-SeabiscuitGrayThemeColor'
            )}
          />
          {!!errors?.name?.message ? (
            <MessageHelperComp
              className="absolute z-0 bottom-0 left-0 translate-y-full"
              isError={true}
              message={errors?.name?.message}
            />
          ) : null}
        </div>
        <div className="pt-5 mt-5 border-t border-t-SeabiscuitLightThemeColorD3">
          <div className="py-3 px-3 relative flex items-center justify-between bg-SeabiscuitGrayThemeColor rounded-md">
            <FenceHeight
              editable={editableScore}
              errors={errors}
              watch={watch}
              setValue={setValue}
              trigger={trigger}
            />
            <JumpOffToggle
              className="ml-10 pl-10 border-l border-l-SeabiscuitLightThemeColorD3"
              watch={watch}
              setValue={setValue}
              editable={editableScore}
            />
          </div>
        </div>
        <div className="pt-1 mt-5 border-t border-t-SeabiscuitLightThemeColorD3">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              disabled={!editableScore}
              items={watch('sections')
                .filter((section) => section.type !== 'jump off')
                .map(({ id }) => id)}
              strategy={verticalListSortingStrategy}
            >
              {watch('sections')
                .filter((section) => section.type !== 'jump off')
                .map((section, index) => (
                  <ScoreCardSection
                    key={section.id}
                    type={dataToPassOn.type}
                    index={index}
                    section={section}
                    register={register}
                    watch={watch}
                    errors={errors}
                    setValue={setValue}
                    editable={editableScore}
                  />
                ))}
            </SortableContext>
          </DndContext>
        </div>
        <div className="mt-4 flex items-center justify-between gap-2 text-[14px] text-SeabiscuitDark200ThemeColor/50">
          <div className="flex items-center gap-2">
            <img src={`/assets/og_icons/Cancel.svg`} className="rotate-45 w-5" alt="plus icon" />
            <button
              disabled={!editableScore}
              type="button"
              className="underline hover:no-underline"
              onClick={onAddSection}
            >
              Add Round
            </button>
          </div>
          {watch('sections').filter((section) => section.type !== 'jump off').length > 0 && (
            <button
              disabled={!editableScore}
              type="button"
              className="underline hover:no-underline"
              onClick={onRemoveAllSections}
            >
              Delete All
            </button>
          )}
        </div>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={watch('sections')
              .filter((section) => section.type === 'jump off')
              .map(({ id }) => id)}
            strategy={verticalListSortingStrategy}
          >
            {watch('showJumpOff') &&
              watch('sections').filter((section) => section.type === 'jump off').length > 0 && (
                <div className="pt-1 mt-5 border-t border-t-SeabiscuitLightThemeColorD3">
                  {watch('sections')
                    .filter((section) => section.type === 'jump off')
                    .map((section, index) => (
                      <ScoreCardSection
                        key={section.id}
                        type={dataToPassOn.type}
                        index={
                          watch('sections').filter((section) => section.type !== 'jump off')
                            .length + index
                        }
                        section={section}
                        register={register}
                        watch={watch}
                        errors={errors}
                        setValue={setValue}
                        editable={editableScore}
                      />
                    ))}
                </div>
              )}
          </SortableContext>
        </DndContext>
      </div>
    </MainModal>
  )
}
